'use strict';
import { cn_camera } from '../svg/cn_camera';
import { logger } from '../utils/cn_logger';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Space class
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//**** Internal : measure points
//***********************************************************************************
import {
    cn_add, cn_cart,
    cn_clone,
    cn_dist,
    cn_dot,
    cn_intersect_circles,
    cn_intersect_line,
    cn_mul,
    cn_normal,
    cn_normalize, cn_polar,
    cn_size,
    cn_sub
} from '../utils/cn_utilities';
import { cn_image_dir } from '../utils/image_dir';
import { cn_space_segment } from './cn_space_segment';

export class space_measure_point {
    /**
     * Constructor
     * @param {cn_space_segment} space_segment
     * @param {number} contour
     */
    constructor(space_segment, contour) {
        this.space_segment = space_segment;
        this.contour = contour;
        this.p0 = [0, 0];
        this.p1 = [0, 0];
        this.position = [0, 0];
        this.selected = false;
        this.length = 0;
        this.locked = false;
        this.update();
    }

    /**
     *
     * @param {cn_camera} camera
     * @param {Array<string>} extra_classes
     * @returns
     */
    draw(camera, extra_classes = []) {
        let html = "";
        this.length = cn_dist(this.p0, this.p1);
        html += camera.draw_measure(this.p0, this.p1, (this.locked) ? null : this.position, this.selected && !this.locked, true, extra_classes);
        html += camera.draw_line(this.p0, this.p1, "space_measure_contour");
        return html;
    }

    update() {
        this.p0 = cn_clone(this.space_segment.points[0]);
        this.p1 = cn_clone(this.space_segment.points[1]);
    }
}

class space_diagonal {
    constructor() {
        this.length = 0;
        this.position = [0, 0];
        this.selected = false;
        this.locked = false;
    }
}

const DIAGONAL_TOLERANCE = 0.0005;

//***********************************************************************************
//**** Space class
//***********************************************************************************
export class cn_space_measure {
    constructor(space) {
        this.space = space;

        //*** Model data
        this.diagonal_origin = 0;

        //*** measure points */
        this.contours = [];
        this.measure_points = [];
        this.diagonal_point = [0, 0];
        this.diagonals = [];

        this.radius = 20;

        this._selected_origin = -1;

        this._first_lock = -10;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = {};
        json.diagonal_origin = this.diagonal_origin;
        return json;
    }

    static unserialize(json, space) {
        space.measure = new cn_space_measure(space);

        if (typeof (json.diagonal_origin) == 'number')
            space.measure.diagonal_origin = json.diagonal_origin;
    }

    //***********************************************************************************
    //**** update geometry
    //***********************************************************************************
    update() {
        this.measure_points = [];
        this.contours = [];
        this.space.space_segments.forEach((ss_list, nctr) => {
            let contour = ss_list.filter(ss => ss.wall && ss.wall_side < 2).map(ss => new space_measure_point(ss, nctr));
            this.contours.push(contour);
            this.measure_points = this.measure_points.concat(contour);

            //*** Compute locks */
            if (nctr) contour.forEach(mp => mp.locked = true);
            else {
                contour.forEach(mp => mp.locked = mp.space_segment.wall.locked);
                let sz = contour.length;
                while (true) {
                    let change = false;
                    contour.forEach((mp, i) => {
                        if (!mp.locked && contour[(i + 1) % sz].locked && contour[(i + sz - 1) % sz].locked) {
                            change = true;
                            mp.locked = true;
                        }
                    });
                    if (!change) break;
                }
            }
        });
        this.diagonals = [];
        if (this.contours.length == 0 || this.contours[0].length == 0) return;
        let sz = this.contours[0].length;
        if (this.diagonals.length != sz - 3) {
            for (var i = 0; i < sz - 3; i++)
                this.diagonals.push(new space_diagonal());
        }
        this._update_diagonals();
    }

    _update_diagonals() {

        //*** Find diagonals */
        let best_distance = cn_dist(this.diagonal_point, this.contours[0][0].p0);
        this.diagonal_origin = 0;
        this.contours[0].forEach((mp, i) => {
            let x = cn_dist(this.diagonal_point, mp.p0);
            if (x < best_distance) {
                best_distance = x;
                this.diagonal_origin = i;
            }
        });
        this.diagonal_point = this.contours[0][this.diagonal_origin].p0;

        //*** Update locks */
        let sz = this.contours[0].length;
        let first_lock = -10;
        let last_lock = -10;
        for (var i = 0; i < sz; i++) {
            if (!this.contours[0][(i + this.diagonal_origin) % sz].space_segment.wall.locked)
                continue;
            if (first_lock < 0) first_lock = i;
            last_lock = i;
        }
        if (first_lock == 1) first_lock = 0;
        if (last_lock == sz - 2) last_lock = sz - 1;
        for (var i = 0; i < sz; i++)
            this.contours[0][(i + this.diagonal_origin) % sz].locked = (i >= first_lock && i <= last_lock);

        this._first_lock = first_lock;

        //*** diagonal locks */
        for (var i = 1; i < sz - 2; i++) {
            this.diagonals[i - 1].locked = (i >= first_lock - 1 && i <= last_lock);
            this.diagonals[i - 1].length = cn_dist(this.contours[0][(i + 1 + this.diagonal_origin) % sz].p0, this.diagonal_point);
        }
    }

    //***********************************************************************************
    //**** draw
    //***********************************************************************************
    draw_measures(camera, extra_classes = []) {
        var html = '';
        if (this.measure_points.length == 0) return html;

        //*** draw measure points */
        for (var i in this.measure_points)
            html += this.measure_points[i].draw(camera, extra_classes);

        return html;
    }

    //***********************************************************************************
    //**** draw
    //***********************************************************************************
    draw(camera) {
        var html = '';

        if (!camera.show_space_measure) return html;

        let active_contour_index = this.measure_points[this.diagonal_origin].contour;
        let active_contour = this.contours[active_contour_index];

        //*** draw measure points */
        for (var i = 0; i < this.measure_points.length; i++) {
            html += this.measure_points[i].draw(camera);
        }

        let sz = active_contour.length;
        for (var i = 2; i <= sz - 2; i++) {
            var p = active_contour[(i + this.diagonal_origin) % sz].p0;
            var diag = this.diagonals[i - 2];
            html += camera.draw_measure(this.diagonal_point, p, (diag.locked) ? null : diag.position, diag.selected && !diag.locked, false);
        }

        //*** Draw position of diagonal origin */
        var pp = camera.world_to_screen(this.diagonal_point);
        html += '<circle class=\'space_measure_origin\' cx=\'' + pp[0] + '\' cy=\'' + pp[1] + '\' r=\'' + this.radius + '\'/>';
        html += '<image xlink:href=\'' + cn_image_dir() + 'lock.svg\' x=\'' + (pp[0] - this.radius) + '\' y=\'' + (pp[1] - this.radius) + '\' width=\'' + (this.radius * 2) + '\' height=\'' + (this.radius * 2) + '\' preserveAspectRatio=\'xMidYMid meet\'/>';

        //*** Draw ghost of next diagonal origin */
        if (this._selected_origin >= 0) {
            pp = camera.world_to_screen(this.measure_points[this._selected_origin % sz].p0);
            html += '<circle class=\'space_measure_origin ghost\' cx=\'' + pp[0] + '\' cy=\'' + pp[1] + '\' r=\'' + this.radius + '\'/>'
            html += '<image xlink:href=\'' + cn_image_dir() + 'lock.svg\' x=\'' + (pp[0] - this.radius) + '\' y=\'' + (pp[1] - this.radius) + '\' width=\'' + (this.radius * 2) + '\' height=\'' + (this.radius * 2) + '\' preserveAspectRatio=\'xMidYMid meet\' opacity=\'0.5\' />';
        }

        return html;
    }

    //***********************************************************************************
    //**** Check selection
    //***********************************************************************************
    is_mouseover(world_point, screen_point, camera) {
        //*** clear */
        for (var i = 0; i < this.measure_points.length; i++)
            this.measure_points[i].selected = false;
        for (var i = 0; i < this.diagonals.length; i++)
            this.diagonals[i].selected = false;

        this._selected_measure = -1;
        this._selected_diagonal = -1;
        this._selected_origin = -1;

        var tolerance = 10;
        for (var i = 0; i < this.measure_points.length; i++) {
            if (this.measure_points[i].locked) continue;
            if (cn_dist(this.measure_points[i].position, screen_point) > tolerance) continue;
            this.measure_points[i].selected = true;
            this._selected_measure = i;
            return true;
        }
        for (var i = 0; i < this.diagonals.length; i++) {
            if (this.diagonals[i].locked) continue;
            if (cn_dist(this.diagonals[i].position, screen_point) > tolerance) continue;
            this.diagonals[i].selected = true;
            this._selected_diagonal = i;
            return true;
        }

        var tolerance = camera.screen_to_world_scale * this.radius;
        for (var i = 0; i < this.measure_points.length; i++) {
            if (cn_dist(this.measure_points[i].p0, world_point) > tolerance) continue;
            this._selected_origin = i;
            return true;
        }

        return false;
    }

    //***********************************************************************************
    //**** Info on selection
    //***********************************************************************************
    diagonal_origin_selected() {
        return (this._selected_origin >= 0);
    }

    //***********************************************************************************
    //**** Click management
    //***********************************************************************************
    click() {
        if (this._selected_measure >= 0) {
            if (this._selected_measure >= this.measure_points.length) return false;
            var mp = this.measure_points[this._selected_measure];
            return cn_dist(mp.p0, mp.p1);
        }

        if (this._selected_diagonal >= 0) {
            if (this._selected_diagonal >= this.diagonals.length) return false;
            return this.diagonals[this._selected_diagonal].length;
        }

        if (this._selected_origin >= 0) {
            this.diagonal_point = this.contours[0][this._selected_origin].p0;
            this._update_diagonals();
            return true;
        }
        return false;
    }

    //***********************************************************************************
    //**** Return impiled vertices
    //***********************************************************************************
    get_implied_vertices() {
        var vertices = [];
        return vertices;
    }

    get_implied_walls() {
        var walls = [];
        return walls;
    }

    //***********************************************************************************
    //**** set measure value
    //***********************************************************************************
    _intersect_circle(c0, r0, c1, r1, p) {
        let inters = cn_intersect_circles(c0, r0, c1, r1);
        if (inters.length == 0) return null;
        if (inters.length == 1) return inters[0];
        if (cn_dist(p, inters[0]) < cn_dist(p, inters[1]))
            return inters[0];
        return inters[1];
    }

    _compute_rotation(p0, p1) {
        let dx = cn_sub(p0, this.diagonal_point);
        cn_normalize(dx);
        let dy = cn_normal(dx);
        let d1 = cn_sub(p1, this.diagonal_point);
        cn_normalize(d1);
        return Math.asin(cn_dot(dy, d1));
    }

    _rotate(p, alpha) {
        let d = cn_sub(p, this.diagonal_point);
        let cosalpha = Math.cos(alpha);
        let sinalpha = Math.sin(alpha);
        return cn_add(this.diagonal_point, [d[0] * cosalpha - d[1] * sinalpha, d[0] * sinalpha + d[1] * cosalpha]);
    }

    set_measure(v, transaction_manager = null) {
        let res = this._set_measure(v, transaction_manager);
        this.update();
        return res;
    }
    _set_measure(v, transaction_manager) {
        let origin_measure = this.measure_points[this.diagonal_origin];
        let contour = this.contours[origin_measure.contour];
        if (contour.length < 3) return false;

        //*** Sets a regular measure */
        let measure = null;
        if (this._selected_measure >= 0)
            measure = this.measure_points[this._selected_measure];
        else
            measure = contour[(1 + this._selected_diagonal + this.diagonal_origin) % contour.length];

        if (measure.contour != origin_measure.contour) return false;
        let i0 = contour.indexOf(measure);
        let i1 = contour.indexOf(origin_measure);
        let next_diagonal = cn_dist(this.diagonal_point, measure.p1);

        //*** Did we set a diagonal ?  */
        if (this._selected_diagonal >= 0) {
            next_diagonal = v;
        }
        //*** Special case for the first segment of the contour */
        else if (i0 == i1) {
            let dir = cn_sub(measure.p1, measure.p0);
            let vp = cn_normalize(dir);
            measure.p1 = cn_add(measure.p0, cn_mul(dir, v));
            i0 = ((i0 + 1) % contour.length);
            measure = contour[i0];
            measure.p0 = cn_add(measure.p0, cn_mul(dir, v - vp));
            next_diagonal = cn_dist(this.diagonal_point, measure.p1);
        }
        //*** Special case for the last segment of the contour */
        else if ((i0 + 1) % contour.length == i1) {
            let dir = cn_sub(measure.p0, measure.p1);
            cn_normalize(dir);
            measure.p0 = cn_add(measure.p1, cn_mul(dir, v));
            i0 = ((i0 + contour.length - 1) % contour.length);
            measure = contour[i0];
            next_diagonal = v;
        }
        //*** Regular case */
        else
            measure.length = v;

        //*** Compute new position of second point of measure */
        let p1 = this._intersect_circle(measure.p0, measure.length, this.diagonal_point, next_diagonal, measure.p1);
        if (!p1) return false;

        //*** Compute rotation of following segments */
        let alpha = this._compute_rotation(measure.p1, p1);
        measure.p1 = p1;

        if (this._selected_diagonal >= 0) {
            i0 = (i0 + 1) % contour.length;
            measure = contour[i0];
            let d0 = cn_sub(measure.p0, this.diagonal_point);
            cn_normalize(d0);
            measure.p0 = cn_add(this.diagonal_point, cn_mul(d0, v));
            measure.p0 = this._rotate(measure.p0, alpha);

            let p1 = this._intersect_circle(measure.p0, measure.length, this.diagonal_point, cn_dist(this.diagonal_point, measure.p1), measure.p1);
            if (!p1) return false;
            alpha = this._compute_rotation(measure.p1, p1);
            measure.p1 = p1;
        }

        if (i1 < i0) i1 += contour.length;
        for (var i = i0 + 1; i < i1; i++) {
            let m = contour[i % contour.length];
            m.p0 = this._rotate(m.p0, alpha);
            m.p1 = this._rotate(m.p1, alpha);
        }
        if ((i0 + contour.length - this.diagonal_origin) % contour.length < this._first_lock) {
            for (var ii = 0; ii < contour.length; ii++) {
                let m = contour[ii];
                m.p0 = this._rotate(m.p0, -alpha);
                m.p1 = this._rotate(m.p1, -alpha);
            }
        }

        this.measure_points.forEach(m => m.length = cn_dist(m.p0, m.p1));
        this.diagonals = [];

        //*** Check wall consistency */
        let impacted_walls = [];
        let impacted_wall_measures = [];
        if (contour.some(measure => {
            let wall = measure.space_segment.wall;
            if (!impacted_walls.includes(wall)) {
                impacted_walls.push(wall);
                impacted_wall_measures.push(measure);
            }
            let ctrs = contour.filter(m => m.space_segment.wall == wall);
            if (ctrs.length < 2) return false;
            let nor = cn_normal(cn_sub(measure.p1, measure.p0));
            cn_normalize(nor);
            return ctrs.some(c => Math.abs(cn_dot(nor, cn_sub(c.p1, measure.p0))) > 0.01);
        })) {
            return false;
        }

        let new_wall_axis = [];
        impacted_walls.forEach((wall, i) => {
            let measure = impacted_wall_measures[i];
            let dir = cn_sub(measure.p1, measure.p0);
            cn_normalize(dir);
            let y = (measure.space_segment.wall_side == 0) ? -wall.bounds.y0 : wall.bounds.y1;
            let orig = cn_add(measure.p0, cn_mul(cn_normal(dir), y));
            new_wall_axis.push([orig, dir]);
        });

        //*** Apply changes */
        let impacted_vertices = [];
        let impacted_vertices_positions = [];
        let cannot_compute = false;
        impacted_walls.forEach((wall, i) => {
            if (cannot_compute) return;
            let ax0 = new_wall_axis[i];
            wall.vertices.forEach(vertex => {
                if (cannot_compute) return;
                if (impacted_vertices.includes(vertex)) return;
                impacted_vertices.push(vertex);
                let other_wall = vertex.support_wall;
                if (!other_wall && vertex.walls.length > 1)
                    other_wall = (vertex.walls[0] == wall) ? vertex.walls[1] : vertex.walls[0];
                let ax1 = [];
                if (other_wall) {
                    let j = impacted_walls.indexOf(other_wall);
                    if (j >= 0)
                        ax1 = new_wall_axis[j];
                    else
                        ax1 = [other_wall.vertices[0].position, other_wall.bounds.direction];
                }
                else
                    ax1 = [vertex.position, cn_normal(ax0[1])];
                let p = cn_intersect_line(ax0[0], ax0[1], ax1[0], ax1[1]);
                if (p)
                    impacted_vertices_positions.push(p);
                else
                    cannot_compute = true;
            });
        });

        if (cannot_compute) return false;

        let scene = this.space.scene;
        if (transaction_manager)
            transaction_manager.push_transaction("Triangulation");

        impacted_vertices.forEach((vertex, i) => {
            if (transaction_manager)
                transaction_manager.push_item_set(vertex, "position");
            vertex.position = impacted_vertices_positions[i];
        });

        scene.full_update();
        return true;
    }
}

